import { useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';

import { useLoggedInUser } from 'src/services/api/auth/use-logged-in-user';
import { queryKeys } from 'src/services/api/constants';
import {
    LEAD_POLL_INTERVAL_MS,
    usePollLeads,
} from 'src/services/api/leads/use-poll-leads';
import { usePollConversations } from 'src/services/api/messages/use-poll-conversations';
import { subscribeToFrames } from 'src/services/websocket/live-updates-signal';
import { useIsLive } from 'src/services/websocket/use-is-live';
import { useLiveRefetch } from 'src/services/websocket/use-live-refetch';
import { useWebSocket } from 'src/services/websocket/use-websocket';
import { isMessageFrame } from 'src/services/websocket/ws-protocol';
import {
    refetchAllActiveLeadState,
    refetchLeadCollection,
    useLeadLiveUpdates,
} from 'src/components/live-updates-reaction/use-lead-live-updates';

export default function LiveUpdatesHost() {
    useWebSocket();
    const isLive = useIsLive();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = useLoggedInUser();
    usePollLeads(loggedInUser, isLive);
    const conversations = usePollConversations(loggedInUser, isLive);
    const previousIsLiveRef = useRef(isLive);
    // A host that mounts offline holds caches exactly as old as itself, so its
    // first connect is measured like any other outage.
    const disconnectedAtRef = useRef<number | null>(isLive ? null : Date.now());
    useLeadLiveUpdates();

    useEffect(() => {
        const wasLive = previousIsLiveRef.current;
        previousIsLiveRef.current = isLive;
        if (!isLive && wasLive) {
            disconnectedAtRef.current = Date.now();
            void refetchLeadCollection(queryClient);
            return;
        }
        if (isLive && !wasLive) {
            const disconnectedAt = disconnectedAtRef.current;
            const shouldRefetchDependents = disconnectedAt !== null &&
                Date.now() - disconnectedAt >= LEAD_POLL_INTERVAL_MS;
            disconnectedAtRef.current = null;
            void (
                shouldRefetchDependents ?
                    refetchAllActiveLeadState(queryClient) :
                    refetchLeadCollection(queryClient)
            );
        }
    }, [isLive, queryClient]);

    useEffect(() => subscribeToFrames((frame) => {
        if (isMessageFrame(frame)) {
            void queryClient.invalidateQueries(
                [queryKeys.messages, frame.lead_uuid],
                { refetchActive: false, refetchInactive: false },
            );
        }
    }), [queryClient]);

    useLiveRefetch({
        isLive,
        enabled: conversations.enabled,
        refetch: conversations.refetch,
        shouldRefetchFrame: (frame) => frame.type === 'message.created',
    });

    return null;
}
